import { ArrowDownward } from '@mui/icons-material'
import { Table as MuiTable, Paper, styled, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { Loader } from 'components/Loader'
import { AvailabilityConstraintHistoriesPageResponse } from 'hooks/useAvailabilityConstraintHistories'
import { theme } from 'modules/App/Providers/Theme/theme'
import { useTranslation } from 'react-i18next'
import { AvailabilityConstraintHistory } from 'types'
import { HistoryItem } from './HistoryItem'

const HeaderCell = styled(TableCell)`
  font-size: 1rem;
  font-weight: 500;
  line-height: 2rem;
`

const Table = styled(MuiTable)(({ theme }) => ({
  '& tbody > tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.grey[50],
  },
}))

type Props = {
  isError: boolean
  isLoading: boolean
  history?: AvailabilityConstraintHistoriesPageResponse
}

const COLUMN_COUNT = 8

const HistoryTable = ({ isError, isLoading, history }: Props) => {
  const { t } = useTranslation()

  let status: 'success' | 'error' | 'loading' | 'no-data' = 'loading'

  switch (true) {
    case isError:
      status = 'error'
      break
    case isLoading:
      status = 'loading'
      break
    case !history?.data.length:
      status = 'no-data'
      break
    case Boolean(history?.data.length):
      status = 'success'
      break
    default:
      break
  }

  return (
    <TableContainer component={Paper} elevation={0} variant="elevation">
      <Table>
        <TableHead>
          <TableRow>
            <HeaderCell align="left">{t('common.label.appointmentType')}</HeaderCell>
            <HeaderCell align="center">{t('common.label.startDate')}</HeaderCell>
            <HeaderCell align="center">{t('common.label.from')}</HeaderCell>
            <HeaderCell align="center">{t('common.label.to')}</HeaderCell>
            <HeaderCell align="center">{t('common.label.endDate')}</HeaderCell>
            <HeaderCell align="left">{t('common.label.occurrence')}</HeaderCell>
            <HeaderCell> </HeaderCell>
            <HeaderCell align="left" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>{t('common.label.date')}</span>
              <ArrowDownward />
            </HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {status === 'error' && (
            <TableRow key="error">
              <TableCell align="center" colSpan={COLUMN_COUNT}>
                <Typography color={theme.palette.error.main} fontWeight={400}>
                  {t('pages.settings.resources.availabilityConstraints.modal.constraints_history.error')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {status === 'loading' && (
            <TableRow key="loading">
              <TableCell align="center" colSpan={COLUMN_COUNT}>
                <div style={{ display: 'grid', placeItems: 'center', height: '1.5rem' }}>
                  <Loader />
                </div>
              </TableCell>
            </TableRow>
          )}
          {status === 'no-data' && (
            <TableRow key="no-data">
              <TableCell align="center" colSpan={COLUMN_COUNT}>
                <Typography color={theme.palette.grey[400]} fontWeight={400}>
                  {t('pages.settings.resources.availabilityConstraints.modal.constraints_history.no_data')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {status === 'success' &&
            history?.data.map((constraint: AvailabilityConstraintHistory) => {
              const user =
                history.users.get(constraint.created_by.id ?? '') ??
                history.users.get(constraint.created_by.email ?? '') ??
                history.users.get(constraint.created_by.gid ?? '') ??
                undefined

              return <HistoryItem key={constraint.created_at} history={constraint} user={user} system={constraint.created_by.system} />
            })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export { COLUMN_COUNT, HistoryTable }
